import { notifications } from "@mantine/notifications"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthenticatedFetch } from "../../hooks/useAuthenticatedFetch.js"
import { useTranslation } from "react-i18next"
import { GlobalStateContext } from "./GlobalStateProvider.jsx"

export const defaultBackgroundJobsState = {
	jobs: [],
}

export const BackgroundJobsStateContext = React.createContext(
	defaultBackgroundJobsState,
)

export const BackgroundJobsStateProvider = ({ children }) => {
	const [backgroundJobsState, setBackgroundJobsState] = React.useReducer(
		(state, newValue) => {
			return {
				...state,
				...newValue,
			}
		},
		defaultBackgroundJobsState,
	)

	const [shownNotifications, setShownNotifications] = useState([])
	const navigate = useNavigate()
	const authenticatedFetch = useAuthenticatedFetch()
	const { t } = useTranslation()
	const { state, dispatch } = useContext(GlobalStateContext)

	const fetchJobs = () => {
		authenticatedFetch("/api/productFetcher/getBackgroundJobs")
			.then((res) => res.json())
			.then((res) => {
				if (res?.jobs) {
					setBackgroundJobsState({ jobs: res.jobs || [] })
				}
			})
			.catch((err) => {
				console.error(err)
			})
	}

	const dismiss = (id) => {
		setBackgroundJobsState({
			jobs: backgroundJobsState.jobs.map((job) => {
				if (job.id === id) {
					return {
						...job,
						dismissed: true,
					}
				} else return job
			}),
		})

		authenticatedFetch("/api/productFetcher/dismissBackgroundJob", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ productHash: id }),
		}).catch((err) => {
			console.error(err)
		})
	}

	const openProduct = (id) => {
		navigate(`/single?productHash=${id}`)
	}

	const createProductPage = (id) => {
		navigate(`/pages?productHash=${id}`)
	}

	useEffect(() => {
		if (!state.accessToken) return
		fetchJobs()

		const interval = setInterval(() => {
			fetchJobs()
		}, 1000 * 30)

		return () => {
			clearInterval(interval)
		}
	}, [state.accessToken])

	useEffect(() => {
		const newShownNotifications = [...shownNotifications]

		backgroundJobsState.jobs.map((job) => {
			if (
				job?.dismissed === true ||
				job?.status !== "completed" ||
				shownNotifications.includes(job.id)
			)
				return null
			const title = job.product.title || ""

			notifications.show({
				title: (
					<div
						className="truncate"
						style={{
							fontSize: "0.85rem",
							color: "#222",
							lineHeight: "1.1",
						}}
					>
						{title.substring(0, 50)}...
					</div>
				),
				message: (
					<div
						style={{
							fontSize: "0.75rem",
							color: "#777",
							textAlign: "start",
						}}
					>
						<div>{t("single.is_ready_to_be_imported")}</div>
						<div
							style={{
								display: "flex",
								gap: "0.25rem",
								justifyContent: "flex-end",
								marginTop: "0.6rem",
								marginBottom: "0.2rem",
							}}
						>
							<button
								className="shadow"
								style={{
									padding: "0.4rem 0.8rem",
									border: "none",
									background: "#222",
									borderRadius: "0.75rem",
									cursor: "pointer",
								}}
								onClick={() => openProduct(job.id)}
							>
								<span
									style={{
										fontSize: "0.8rem",
										fontWeight: "500",
										color: "#fff",
									}}
								>
									{t("single.import_product")}
								</span>
							</button>
							<button
								className="shadow"
								style={{
									padding: "0.4rem 0.8rem",
									border: "none",
									background: "#222",
									borderRadius: "0.75rem",
									cursor: "pointer",
								}}
								onClick={() => createProductPage(job.id)}
							>
								<span
									style={{
										fontSize: "0.8rem",
										fontWeight: "500",
										color: "#fff",
									}}
								>
									{t("ai.create_product_page")}
								</span>
							</button>
						</div>
					</div>
				),
				withCloseButton: true,
				autoClose: false,
				withBorder: true,
				radius: "lg",
				icon: (
					<img
						className="border shadow"
						style={{
							width: "2.3rem",
							height: "2.3rem",
							borderRadius: "0.5rem",
						}}
						src={job.product.image.src}
					></img>
				),
				onClose: () => dismiss(job.id),
			})

			newShownNotifications.push(job.id)
		})

		setShownNotifications(newShownNotifications)
	}, [backgroundJobsState])

	return (
		<BackgroundJobsStateContext.Provider
			value={{ backgroundJobsState, setBackgroundJobsState }}
		>
			{children}
		</BackgroundJobsStateContext.Provider>
	)
}
